/**
 * Velocity Factor - Collection Grid Fix
 *
 * Fixes the product grid layout on collection pages by removing the conflicting
 * `.grid` class from Horizon theme that creates a 14-column centered layout.
 * The `.product-grid` class provides the correct responsive grid behavior.
 *
 * Also applies 1:1 aspect ratio to thumbnail containers with object-fit: contain.
 *
 * Responsive breakpoints:
 * - > 1440px: 4 columns (ultra wide monitors)
 * - > 960px:  3 columns
 * - <= 960px: 2 columns (phones / tablets)
 *
 * Phones get tighter gaps; the card styling for that layout lives in
 * vf_convermax.css ("MOBILE PRODUCT CARDS").
 */
(function() {
  'use strict';

  /**
   * Get the appropriate number of columns based on viewport width
   * @returns {number}
   */
  function getColumnCount() {
    var width = window.innerWidth;
    if (width > 1440) return 4;
    if (width > 960) return 3;
    return 2;
  }

  /**
   * Phones use the compact card layout, which needs tighter gaps
   * @returns {boolean}
   */
  function isCompactGrid() {
    return window.innerWidth <= 749;
  }

  /**
   * Fix the product grid by removing the conflicting .grid class
   * and applying correct responsive grid columns
   */
  function fixProductGrid() {
    // Target grids that still have .grid class (first run)
    var gridsWithClass = document.querySelectorAll('.product-grid.cmRepeater_items.grid');
    gridsWithClass.forEach(function(grid) {
      grid.classList.remove('grid');
    });

    // Apply column settings to all Convermax product grids
    var allGrids = document.querySelectorAll('.product-grid.cmRepeater_items');
    var columns = getColumnCount();
    var compact = isCompactGrid();

    allGrids.forEach(function(grid) {
      // Apply correct grid template columns via inline style to ensure override.
      // minmax(0, 1fr) lets long titles wrap instead of widening a column.
      grid.style.setProperty('grid-template-columns', 'repeat(' + columns + ', minmax(0, 1fr))', 'important');
      grid.style.setProperty('grid-auto-flow', 'row', 'important');
      grid.style.setProperty('column-gap', compact ? '10px' : '20px', 'important');
      grid.style.setProperty('row-gap', compact ? '10px' : '32px', 'important');
    });
  }

  /**
   * Fix thumbnail containers to use 1:1 aspect ratio with object-fit: contain
   * DOM structure: .cmRepeater_items li.item .card-wrapper .card__media .media--transparent img
   */
  function fixThumbnails() {
    // Target the card media containers within Convermax product grid items
    const mediaContainers = document.querySelectorAll('.cmRepeater_items .card__media');

    mediaContainers.forEach(function(container) {
      container.style.setProperty('aspect-ratio', '1 / 1', 'important');
      container.style.setProperty('overflow', 'hidden', 'important');
    });

    // Target the media wrapper divs
    const mediaWrappers = document.querySelectorAll('.cmRepeater_items .media--transparent, .cmRepeater_items .media--hover-effect');

    mediaWrappers.forEach(function(wrapper) {
      wrapper.style.setProperty('width', '100%', 'important');
      wrapper.style.setProperty('height', '100%', 'important');
      wrapper.style.setProperty('display', 'flex', 'important');
      wrapper.style.setProperty('align-items', 'center', 'important');
      wrapper.style.setProperty('justify-content', 'center', 'important');
    });

    // Target the images within product cards to use contain instead of cover/fill
    const images = document.querySelectorAll('.cmRepeater_items .card__media img');

    images.forEach(function(img) {
      img.style.setProperty('object-fit', 'contain', 'important');
      img.style.setProperty('width', '100%', 'important');
      img.style.setProperty('height', '100%', 'important');
      img.style.setProperty('max-width', '100%', 'important');
      img.style.setProperty('max-height', '100%', 'important');
    });
  }

  /**
   * Apply all fixes
   */
  function applyFixes() {
    fixProductGrid();
    fixThumbnails();
  }

  /**
   * Set up a MutationObserver to catch dynamically added/updated grids
   * (e.g., when Convermax re-renders after filtering or pagination)
   */
  function observeGridChanges() {
    const observer = new MutationObserver(function(mutations) {
      let shouldFix = false;

      mutations.forEach(function(mutation) {
        // Check if any added nodes contain product grids or product cards
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for grid with .grid class
              if (node.matches && node.matches('.product-grid.cmRepeater_items.grid')) {
                shouldFix = true;
              } else if (node.querySelector && node.querySelector('.product-grid.cmRepeater_items.grid')) {
                shouldFix = true;
              }
              // Check for new product cards (pagination, filtering)
              if (node.matches && node.matches('.cmRepeater_items li, .cmRepeater_items .card-wrapper')) {
                shouldFix = true;
              } else if (node.querySelector && node.querySelector('.card__media')) {
                shouldFix = true;
              }
            }
          });
        }

        // Check if the grid class was added back to an existing element
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (mutation.target.matches && mutation.target.matches('.product-grid.cmRepeater_items.grid')) {
            shouldFix = true;
          }
        }
      });

      if (shouldFix) {
        // Debounce the fix to avoid excessive calls
        clearTimeout(window._vfGridFixTimeout);
        window._vfGridFixTimeout = setTimeout(applyFixes, 50);
      }
    });

    // Observe the main content area for changes
    const mainContent = document.querySelector('main') || document.body;
    observer.observe(mainContent, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Debounced resize handler to update grid columns on viewport change
   */
  var resizeTimeout;
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      fixProductGrid();
    }, 100);
  }

  // Run fixes when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      applyFixes();
      observeGridChanges();
    });
  } else {
    // DOM is already ready
    applyFixes();
    observeGridChanges();
  }

  // Listen for viewport resize to update column count
  window.addEventListener('resize', handleResize);

  // Also run fixes after a short delay to catch any late-loading content
  setTimeout(applyFixes, 500);
  setTimeout(applyFixes, 1500);

  // ============================================================================
  // Mobile Collapsed YMM Behavior for Collection Pages
  // On mobile, collapse the YMM widget by default when no vehicle is selected.
  // ============================================================================

  var MOBILE_BREAKPOINT = 768;
  var COLLAPSED_CLASS = 'vf-ymm-collapsed';
  var TOGGLE_CLASS = 'vf-ymm-toggle';

  /**
   * Check if we're on a collection page (body has cm_category class)
   */
  function isCollectionPage() {
    return document.body.classList.contains('cm_category');
  }

  /**
   * Check if a vehicle is currently selected in the YMM widget
   * Returns true if Year dropdown has a value selected (not the placeholder)
   */
  function isVehicleSelected() {
    // Prefer the Convermax API: in locked mode (vehicle chosen) the widget
    // renders no <select>, so the DOM check below reports false negatives.
    try {
      if (window.Convermax && typeof window.Convermax.isVehicleSelected === 'function') {
        return window.Convermax.isVehicleSelected();
      }
    } catch (e) {
      /* fall through to DOM check */
    }
    var yearSelect = document.querySelector('.cm_vehicle-widget_select select');
    if (yearSelect) {
      var selectedValue = yearSelect.value;
      // Check if the selected option is not the placeholder (typically empty or "Year")
      return selectedValue && selectedValue !== '' && selectedValue.toLowerCase() !== 'year';
    }
    return false;
  }

  /**
   * Check if we're on mobile
   */
  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /**
   * Initialize the collapsible YMM widget behavior on collection pages
   */
  function initCollapsibleYMM() {
    if (!isCollectionPage()) return;

    var widget = document.querySelector('.cm_vehicle-widget');
    if (!widget) return;

    // Try different header selectors - Convermax uses different structures
    var header = widget.querySelector('.cm_vehicle-widget_header') ||
                 widget.querySelector('.cm_vehicle-widget_title');
    var body = widget.querySelector('.cm_vehicle-widget_body');

    if (!header || !body) return;

    // Only set up once
    if (header.classList.contains(TOGGLE_CLASS)) return;

    // Mark header as toggle and make it clickable
    header.classList.add(TOGGLE_CLASS);
    header.style.cursor = 'pointer';
    header.setAttribute('role', 'button');
    header.setAttribute('aria-expanded', 'true');

    // Add toggle arrow indicator
    var arrow = document.createElement('span');
    arrow.className = 'vf-ymm-arrow';
    arrow.innerHTML = ' ▼';
    arrow.style.cssText = 'font-size: 12px; margin-left: 8px; transition: transform 0.2s ease; display: inline-block;';
    header.appendChild(arrow);

    // Toggle function
    function toggleYMM() {
      var isCollapsed = widget.classList.contains(COLLAPSED_CLASS);

      if (isCollapsed) {
        // Expand
        widget.classList.remove(COLLAPSED_CLASS);
        body.style.display = '';
        body.style.maxHeight = '';
        body.style.overflow = '';
        body.style.opacity = '';
        arrow.style.transform = 'rotate(0deg)';
        header.setAttribute('aria-expanded', 'true');
      } else {
        // Collapse
        widget.classList.add(COLLAPSED_CLASS);
        body.style.display = 'none';
        arrow.style.transform = 'rotate(-90deg)';
        header.setAttribute('aria-expanded', 'false');
      }
    }

    // Click handler
    header.addEventListener('click', function(e) {
      e.preventDefault();
      toggleYMM();
    });

    // Initial state: collapse on mobile if no vehicle selected
    if (isMobile() && !isVehicleSelected()) {
      widget.classList.add(COLLAPSED_CLASS);
      body.style.display = 'none';
      arrow.style.transform = 'rotate(-90deg)';
      header.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Handle resize: auto-expand on desktop, maintain state on mobile
   */
  function handleYMMResize() {
    if (!isCollectionPage()) return;

    var widget = document.querySelector('.cm_vehicle-widget');
    if (!widget) return;

    var body = widget.querySelector('.cm_vehicle-widget_body');
    if (!body) return;

    // On desktop, always expand
    if (!isMobile()) {
      widget.classList.remove(COLLAPSED_CLASS);
      body.style.display = '';
      var arrow = widget.querySelector('.vf-ymm-arrow');
      if (arrow) arrow.style.transform = 'rotate(0deg)';
      var header = widget.querySelector('.cm_vehicle-widget_header');
      if (header) header.setAttribute('aria-expanded', 'true');
    }
  }

  // Initialize collapsible YMM when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // Delay to let Convermax render the widget
      setTimeout(initCollapsibleYMM, 500);
    });
  } else {
    setTimeout(initCollapsibleYMM, 500);
  }

  // Re-initialize when Convermax updates the DOM
  var ymmObserver = new MutationObserver(function(mutations) {
    var shouldInit = false;
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && node.classList.contains('cm_vehicle-widget')) {
              shouldInit = true;
            } else if (node.querySelector && node.querySelector('.cm_vehicle-widget')) {
              shouldInit = true;
            }
          }
        });
      }
    });
    if (shouldInit) {
      setTimeout(initCollapsibleYMM, 100);
    }
  });

  // Observe for YMM widget changes
  if (isCollectionPage()) {
    var mainContent = document.querySelector('main') || document.body;
    ymmObserver.observe(mainContent, { childList: true, subtree: true });
  }

  // Handle resize for YMM collapse behavior
  window.addEventListener('resize', function() {
    clearTimeout(window._vfYMMResizeTimeout);
    window._vfYMMResizeTimeout = setTimeout(handleYMMResize, 150);
  });
})();
